/**
 * Rapier3D backend for the PhysicsWorld interface. (§1.2)
 *
 * Read physics.ts's header first — it states the central decision this file
 * implements: Rapier does NOT own the simulation state, WorldState does. We push
 * POD state in, step, and read contacts back out.
 *
 * ── How that decision is actually enforced here, and what it cost ───────────
 *
 * "Do not let Rapier integrate the ball" is not free. Rapier's pipeline
 * integrates rigid bodies BEFORE the narrow phase runs, so a body that carries a
 * velocity reports its contacts at `p + v·dt`, not at the `p` we pushed in. That
 * is measurable, not theoretical: pushing a ball in at 0.05m of penetration with
 * v = -3 m/s reports dist = -0.10 (the post-integration overlap) rather than
 * -0.05. Enabling the solver corrupts it further — the solver pushes the body out
 * mid-step and rewrites both the depth and the transform.
 *
 * So the ball body is pinned three ways, and all three are load-bearing:
 *
 *   1. gravityScale 0        — ball.ts owns gravity/drag/Magnus (§3.2).
 *   2. linvel/angvel := 0    — with no velocity there is no integration, so the
 *                              manifold is computed at exactly the state we
 *                              pushed in. Depth is then the TRUE penetration.
 *   3. solverGroups := none  — the solver never repositions the ball and never
 *                              applies its own restitution. ball.ts owns the
 *                              bounce (BALL_RESTITUTION_*, §3.2); Rapier only
 *                              reports the normal and depth to bounce WITH.
 *
 * Verified: with (2)+(3), a ball pushed in at 0.05m penetration reports
 * dist = -0.04999999 and the body does not move. That is the contract
 * getBallContacts() is built on.
 *
 * ── The CCD honesty note (§3.4) ─────────────────────────────────────────────
 *
 * BALL_CCD_ENABLED is honoured — setCcdEnabled() is set on the ball body. Be
 * aware of what that does and does not buy in THIS architecture: Rapier's CCD
 * only acts through the constraint solver, and it only alters a body's
 * integrated transform. We deliberately disable the solver (3) and discard the
 * transform, so Rapier's own CCD cannot fire. Measured directly: a ball crossing
 * a net in one tick with CCD on + solver on is caught (contact reported); with
 * CCD on + solver off it tunnels and reports nothing.
 *
 * This is not a gap — it is the design physics.ts already specifies. sweepBall()
 * is the anti-tunnelling mechanism, which is exactly why that method's docstring
 * calls it "mandatory for the ball". The same 72 m/s ball that CCD misses is
 * caught by sweepBall with toi = 0.317. Anti-tunnelling is ball.ts calling
 * sweepBall every tick, NOT a flag on a body. If someone later removes the
 * sweepBall call because "CCD is on", the ball will tunnel through nets and feet.
 *
 * ── Allocation, honestly (§15.7) ────────────────────────────────────────────
 *
 * The tick path here is not allocation-free, and it cannot be. Rapier's JS
 * binding returns a freshly-allocated object from every vector read — there is
 * no scalar accessor for a contact normal at any layer, raw or wrapped. What we
 * do instead:
 *
 *   - Never read a transform back out of Rapier. We wrote it; WorldState has it.
 *   - Use narrowPhase handle-based callbacks (numbers, no Collider wrappers).
 *   - Reuse the raw layer + explicit free() rather than the friendly wrappers,
 *     which halves the garbage and keeps the WASM heap flat.
 *   - Pool every BallContact (never allocated after warmup).
 *
 * Residue: ~2 short-lived RawVectors per ball contact per tick (usually 0-2
 * contacts), plus one Vector per moveCharacter call. Documented rather than
 * hidden.
 */

import RAPIER from '@dimforge/rapier3d-compat';
import type { MatchConfig, WorldState } from './types.js';
import { MAX_PLAYERS } from './types.js';
import {
  type BallContact,
  type PhysicsQueryResult,
  type PhysicsWorld,
  CollisionGroup,
  ContactKind,
  interactionGroups,
  pitchBounds,
} from './physics.js';
import {
  BALL_CCD_ENABLED,
  BALL_RADIUS,
  FIXED_DT,
  GOAL_DEPTH,
  GOAL_HEIGHT,
  GOAL_POST_RADIUS,
  GOAL_WIDTH,
  PLAYER_CAPSULE_HEIGHT,
  PLAYER_CAPSULE_RADIUS,
  QUANT_POS_MAX_X,
  QUANT_POS_MAX_Y,
  QUANT_POS_MAX_Z,
  QUANT_POS_MIN_X,
  QUANT_POS_MIN_Y,
  QUANT_POS_MIN_Z,
} from './constants.js';
import { dsqrt } from '../math/vec3.js';

// ---------------------------------------------------------------------------
// Engine geometry — collision-proxy details, NOT game-feel tunables.
//
// §15.5 says no tunable may be hardcoded in /sim, and these would normally live
// in constants.ts. constants.ts is frozen for this work (other agents are
// editing against it concurrently), so they live here instead. That is a
// deliberate, reported deviation. None of them is a feel parameter — nobody
// tunes the thickness of the ground slab to make shooting feel better, and none
// would ever appear in the leva overlay. If constants.ts unfreezes, they should
// move there wholesale.
//
// Everything derivable from a real constant IS derived below rather than
// duplicated — the ground and boundary volumes fall out of the §2.8
// quantization bounds, which is the correct source: a position outside that box
// cannot even be encoded on the wire, so it is exactly the extent of the
// representable world.
// ---------------------------------------------------------------------------

/** Half-thickness of a net panel. Thin is safe: sweepBall, not thickness, is the
 *  anti-tunnel guarantee (§3.4). */
const NET_HALF_THICKNESS = 0.02;

/** Rapier character-controller skin width. Standard small offset; keeps the
 *  capsule from resting exactly coincident with a surface. */
const CHARACTER_OFFSET = 0.01;

/** Let the controller re-attach to the pitch across small steps so `grounded`
 *  does not flicker on the undulation modelled in §6.3 (PITCH_UNDULATION_M). */
const CHARACTER_SNAP_TO_GROUND = 0.2;

/** Ball contacts reported per tick. Ground + 2 posts + crossbar + 4 net panels +
 *  several players + boundary sensors, with headroom. Overflow drops extra
 *  contacts in Rapier's (deterministic) graph order. */
const BALL_CONTACT_CAPACITY = 16;

// ---------------------------------------------------------------------------
// Interaction group filters (§ physics.ts CollisionGroup)
//
// Rapier's group test is symmetric: A and B interact iff
//   (A.memberships & B.filter) && (B.memberships & A.filter)
// Both sides must agree, so these are declared together to stay legible.
// ---------------------------------------------------------------------------

const BALL_FILTER =
  CollisionGroup.Ground | CollisionGroup.Player | CollisionGroup.Goal |
  CollisionGroup.GoalNet | CollisionGroup.GoalSensor | CollisionGroup.Boundary;

const PLAYER_FILTER =
  CollisionGroup.Ball | CollisionGroup.Ground | CollisionGroup.Goal |
  CollisionGroup.GoalNet | CollisionGroup.Player;

const SOLID_FILTER = CollisionGroup.Ball | CollisionGroup.Player;
const BALL_ONLY_FILTER = CollisionGroup.Ball;

/** Sweeps hit solids only — a sweep that "hit" an invisible sensor volume would
 *  report a phantom obstacle. */
const BALL_SWEEP_FILTER =
  CollisionGroup.Ground | CollisionGroup.Player | CollisionGroup.Goal | CollisionGroup.GoalNet;

/**
 * The character controller collides with static geometry ONLY.
 *
 * Player is excluded on purpose: §3.3 requires soft shoulder-to-shoulder
 * separation and says never hard-lock two capsules. Hard collide-and-slide
 * between players reads as rubber-banding online. stepPlayerCollisions() owns
 * player-vs-player (step.ts stage 8).
 *
 * Ball is excluded on purpose: §3.4's touch model owns ball-vs-player. A player
 * must not bulldoze the ball with a rigid capsule.
 */
const CHARACTER_FILTER =
  CollisionGroup.Ground | CollisionGroup.Goal | CollisionGroup.GoalNet;

/** Tackle/dive reach sweeps DO want players — that is their whole purpose (§2.6). */
const CAPSULE_SWEEP_FILTER =
  CollisionGroup.Player | CollisionGroup.Ground | CollisionGroup.Goal | CollisionGroup.GoalNet;

// ---------------------------------------------------------------------------
// Derived capsule geometry
// ---------------------------------------------------------------------------

/**
 * WorldState player py is the FEET, not the capsule centre.
 *
 * Not a guess: determinism.test.ts spawns standing players with
 * `world.players.py[id] = 0`. A centre-origin convention would put a standing
 * 1.8m player at 0.9. Every conversion in this file goes through this constant.
 */
const PLAYER_CENTRE_OFFSET = PLAYER_CAPSULE_HEIGHT * 0.5;

/** Rapier capsule halfHeight is the CYLINDRICAL segment only: total height is
 *  2*halfHeight + 2*radius. For a 1.8m player at r=0.3 that is 0.6. */
const PLAYER_CAPSULE_HALF_HEIGHT = PLAYER_CAPSULE_HEIGHT * 0.5 - PLAYER_CAPSULE_RADIUS;

/** Rotates a Y-axis cylinder onto the Z axis, for the crossbar. dsqrt(0.5) is
 *  cos(45°)=sin(45°); sqrt is IEEE-correctly-rounded so this is exact on every
 *  engine, unlike dcos(PI/4). */
const CROSSBAR_ROT = { x: dsqrt(0.5), y: 0, z: 0, w: dsqrt(0.5) };
const IDENTITY_ROT = { x: 0, y: 0, z: 0, w: 1 };
const ZERO_VEC = { x: 0, y: 0, z: 0 };

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

interface ColliderInfo {
  kind: ContactKind;
  /** PlayerId for ContactKind.Player, else -1. */
  playerId: number;
}

interface Xyz { x: number; y: number; z: number }

/** Structural view of Rapier's RawVector — the raw layer is not re-exported from
 *  the package root, so we describe only what we touch. */
interface RawVecLike {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  free(): void;
}

/** Copy a RawVector into a scratch and release the WASM allocation. Forgetting
 *  the free() here leaks the WASM heap for the length of a match. */
function readRawVec(raw: RawVecLike | undefined | null, out: Xyz): boolean {
  if (!raw) return false;
  out.x = raw.x;
  out.y = raw.y;
  out.z = raw.z;
  raw.free();
  return true;
}

/**
 * Rotate `v` by quaternion `q` into `out`. Built only from +,-,* so it is
 * bit-identical on every engine (see the note atop vec3.ts). `out` may not alias
 * `v`.
 */
function rotateByQuat(out: Xyz, q: { x: number; y: number; z: number; w: number }, v: Xyz): void {
  // t = 2 * (q_vec × v);  v' = v + w*t + q_vec × t
  const tx = 2 * (q.y * v.z - q.z * v.y);
  const ty = 2 * (q.z * v.x - q.x * v.z);
  const tz = 2 * (q.x * v.y - q.y * v.x);
  out.x = v.x + q.w * tx + (q.y * tz - q.z * ty);
  out.y = v.y + q.w * ty + (q.z * tx - q.x * tz);
  out.z = v.z + q.w * tz + (q.x * ty - q.y * tx);
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Build the Rapier-backed PhysicsWorld.
 *
 * Async because rapier3d-compat must `await RAPIER.init()` before any use. The
 * same WASM loads on Node and in the browser, which is what makes §13.1's
 * Node-vs-headless-Chrome determinism test meaningful.
 *
 * ⚠ CALLER REQUIREMENT: call syncAndStep(state) ONCE after spawning players,
 * before the first step(). moveCharacter() needs to know where the capsules are,
 * and syncAndStep is the only method that receives a WorldState. Until it has
 * run, the capsules sit at the origin. See moveCharacter for the full note.
 */
export async function createPhysicsWorld(config: MatchConfig): Promise<PhysicsWorld> {
  await RAPIER.init();

  // Zero gravity: the ball model owns gravity (§3.2) and players are kinematic.
  // A non-zero world gravity here would silently double-apply it.
  const world = new RAPIER.World(ZERO_VEC);
  world.timestep = FIXED_DT;

  /** Static pitch geometry, torn down and rebuilt by configurePitch. */
  const staticBodies: RAPIER.RigidBody[] = [];
  /** Collider handle → what it is. Rapier handles are bit-cast u64s (handle 1
   *  arrives as 5e-324), so they are NOT array indices — a Map is required. */
  const colliderIndex = new Map<number, ColliderInfo>();

  // ── Ball ────────────────────────────────────────────────────────────────
  const ballBody = world.createRigidBody(
    RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(0, BALL_RADIUS, 0)
      .setGravityScale(0)
      .setCcdEnabled(BALL_CCD_ENABLED)
      // A sleeping body stops refreshing its contacts. We drive this body by
      // teleport, which is exactly the case Rapier's sleep heuristic misreads.
      .setCanSleep(false),
  );
  const ballCollider = world.createCollider(
    RAPIER.ColliderDesc.ball(BALL_RADIUS)
      .setCollisionGroups(interactionGroups(CollisionGroup.Ball, BALL_FILTER))
      // Solver filter 0: generate manifolds, never generate a response.
      .setSolverGroups(interactionGroups(CollisionGroup.Ball, 0)),
    ballBody,
  );
  const ballHandle = ballCollider.handle;

  // ── Players (§3.3) ──────────────────────────────────────────────────────
  const playerBodies: RAPIER.RigidBody[] = [];
  const playerColliders: RAPIER.Collider[] = [];
  /** Mirror of enable/disable so we only touch Rapier when it actually changes. */
  const playerEnabled = new Uint8Array(MAX_PLAYERS);

  for (let i = 0; i < MAX_PLAYERS; i++) {
    const body = world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(0, PLAYER_CENTRE_OFFSET, 0)
        .setCanSleep(false),
    );
    const collider = world.createCollider(
      RAPIER.ColliderDesc.capsule(PLAYER_CAPSULE_HALF_HEIGHT, PLAYER_CAPSULE_RADIUS)
        .setCollisionGroups(interactionGroups(CollisionGroup.Player, PLAYER_FILTER)),
      body,
    );
    playerBodies.push(body);
    playerColliders.push(collider);
    playerEnabled[i] = 1;
  }

  const controller = world.createCharacterController(CHARACTER_OFFSET);
  controller.setUp({ x: 0, y: 1, z: 0 });
  controller.enableSnapToGround(CHARACTER_SNAP_TO_GROUND);
  controller.setSlideEnabled(true);
  // A football pitch is flat. Autostep would let a player walk up a goalpost.
  controller.disableAutostep();
  // The character must never shove the ball — §3.4's touch model owns that.
  controller.setApplyImpulsesToDynamicBodies(false);

  // ── Reusable query shapes (constructing these per call would allocate) ───
  const ballShape = new RAPIER.Ball(BALL_RADIUS);
  const sweepCapsuleShape = new RAPIER.Capsule(PLAYER_CAPSULE_HALF_HEIGHT, PLAYER_CAPSULE_RADIUS);

  // ── Scratch (module-lifetime, never allocated in a tick) ────────────────
  const scratchPos: Xyz = { x: 0, y: 0, z: 0 };
  const scratchVel: Xyz = { x: 0, y: 0, z: 0 };
  const scratchNormal: Xyz = { x: 0, y: 0, z: 0 };
  const scratchLocal: Xyz = { x: 0, y: 0, z: 0 };
  const scratchWorld: Xyz = { x: 0, y: 0, z: 0 };
  const ballRot = { x: 0, y: 0, z: 0, w: 1 };

  // ── Contact pool (§15.7) ────────────────────────────────────────────────
  const contactPool: BallContact[] = [];
  /** Handed out by getBallContacts. Only its `length` is mutated; the pool keeps
   *  permanent references so truncation never drops a pooled object. */
  const contactView: BallContact[] = [];
  let contactCount = 0;

  function acquireContact(): BallContact | null {
    if (contactCount >= BALL_CONTACT_CAPACITY) return null;
    let c = contactPool[contactCount];
    if (c === undefined) {
      c = { kind: ContactKind.None, playerId: -1, nx: 0, ny: 0, nz: 0, px: 0, py: 0, pz: 0, depth: 0 };
      contactPool[contactCount] = c;
    }
    contactCount++;
    return c;
  }

  function commitContactView(): void {
    for (let i = contactView.length; i < contactCount; i++) contactView[i] = contactPool[i]!;
    contactView.length = contactCount;
  }

  /** Live WorldState, captured each syncAndStep. See moveCharacter. */
  let liveState: WorldState | null = null;
  let disposed = false;

  // ---------------------------------------------------------------------------
  // Static geometry
  // ---------------------------------------------------------------------------

  function addStatic(desc: RAPIER.ColliderDesc, kind: ContactKind, membership: CollisionGroup, filter: number): number {
    const body = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    const collider = world.createCollider(desc.setCollisionGroups(interactionGroups(membership, filter)), body);
    staticBodies.push(body);
    colliderIndex.set(collider.handle, { kind, playerId: -1 });
    return collider.handle;
  }

  /**
   * The flat pitch slab's collider handle. sweepBall excludes it from a cast that
   * starts with the ball resting FLUSH on it and not moving downward — in that
   * configuration the slab physically cannot be hit, but Rapier's shape-cast
   * reports it anyway at toi=0 with a degenerate normal (EPA at zero separation),
   * and that spurious smallest-toi hit MASKS any real obstacle (post, player,
   * wall) later in the same cast. Reset by configurePitch (handles are reissued).
   */
  let groundColliderHandle = -1;

  /**
   * Combined sweep-exclusion state for sweepBall (mutated per call, static
   * predicate — no per-call closure). `playerHandle` excludes the CARRIER's own
   * capsule while he possesses the ball: a dribbler's body must not wall off the
   * ball he is shepherding (feet step around a carried ball; the touch model IS
   * that interaction). Measured without it: a knocked ball starting inside/flush
   * with the carrier's capsule loses half its displacement per tick to spurious
   * sweep hits and gets swallowed on sprint carries. Opponents still block.
   */
  /** One tick of gravity-induced descent on a grounded ball (g·dt², + margin):
   *  the "resting descent" a rolling ball shows every tick without ever truly
   *  dropping. Below this the slab exclusion must still apply (see sweepBall). */
  const RESTING_DROP_PER_TICK = 0.004;

  const sweepExclude = { slab: false, playerHandle: -2 };
  const sweepBallPredicate = (c: RAPIER.Collider): boolean => {
    if (sweepExclude.slab && c.handle === groundColliderHandle) return false;
    return c.handle !== sweepExclude.playerHandle;
  };

  /** Rebuild the handle→kind index. Called on every configurePitch, because
   *  removing a body frees its handle and the arena may reissue it. */
  function rebuildIndex(): void {
    colliderIndex.clear();
    colliderIndex.set(ballHandle, { kind: ContactKind.None, playerId: -1 });
    for (let i = 0; i < MAX_PLAYERS; i++) {
      colliderIndex.set(playerColliders[i]!.handle, { kind: ContactKind.Player, playerId: i });
    }
  }

  function configurePitch(cfg: MatchConfig): void {
    // A different pitch is a different match (§9.2). Tear down completely rather
    // than trying to patch the previous layout.
    for (let i = 0; i < staticBodies.length; i++) world.removeRigidBody(staticBodies[i]!);
    staticBodies.length = 0;
    rebuildIndex();

    const bounds = pitchBounds(cfg);

    // ── Ground ────────────────────────────────────────────────────────────
    // Spans the whole representable volume (§2.8 quantization bounds) rather
    // than the pitch: a position outside that box cannot be encoded on the wire,
    // so nothing can ever be beside or beneath this slab. Top face sits at y=0.
    const groundHalfX = (QUANT_POS_MAX_X - QUANT_POS_MIN_X) * 0.5;
    const groundHalfZ = (QUANT_POS_MAX_Z - QUANT_POS_MIN_Z) * 0.5;
    const groundHalfY = -QUANT_POS_MIN_Y * 0.5;
    groundColliderHandle = addStatic(
      RAPIER.ColliderDesc.cuboid(groundHalfX, groundHalfY, groundHalfZ)
        .setTranslation(
          (QUANT_POS_MAX_X + QUANT_POS_MIN_X) * 0.5,
          QUANT_POS_MIN_Y * 0.5,
          (QUANT_POS_MAX_Z + QUANT_POS_MIN_Z) * 0.5,
        ),
      ContactKind.Ground,
      CollisionGroup.Ground,
      SOLID_FILTER,
    );

    // ── No world walls (Phase-1 Step 6) ───────────────────────────────────
    // The encodable volume used to be enclosed by four solid walls at
    // x≈±59/z≈±39 (commit aa02ca7). They violated §5.3 of the recovery brief —
    // the pitch boundary is a RULES boundary, not a physical wall — and were
    // measured bouncing dead balls back toward the pitch from ~5 m beyond the
    // touchline (boundary-behavior Probe 2). Removed. Containment is now
    // layered and rules-driven instead:
    //   1. A live ball that fully crosses a line is collected by the match FSM
    //      the same tick (evaluateBallOut → restart staging).
    //   2. A dead-phase ball (Goal/HalfTime/FullTime) that sails past the line
    //      is DAMPED to rest by stepBall's dead-ball handling
    //      (DEAD_BALL_OUT_MARGIN / DEAD_BALL_DAMP_PER_S) — it visibly rolls
    //      out and stops; nothing ever bounces it back in.
    //   3. The arithmetic failsafe clamps in ball.ts / player-controller.ts
    //      remain the last-resort format invariant (positions outside the §2.8
    //      volume cannot be encoded on the wire). They ZERO velocity — never
    //      reflect — so they cannot masquerade as a wall.
    // ContactKind.Wall stays in the enum for intentional stadium geometry.

    // ── Goals, nets, goal sensors ─────────────────────────────────────────
    // Goal dimensions are fixed by law regardless of pitch size (§9.2).
    const halfGoalW = GOAL_WIDTH * 0.5;
    for (let s = 0; s < 2; s++) {
      const sign = s === 0 ? 1 : -1;
      const lineX = sign * bounds.goalLineX;
      const backX = lineX + sign * GOAL_DEPTH;

      // Posts: cylinders default to the Y axis, so they need no rotation.
      addStatic(
        RAPIER.ColliderDesc.cylinder(GOAL_HEIGHT * 0.5, GOAL_POST_RADIUS)
          .setTranslation(lineX, GOAL_HEIGHT * 0.5, -halfGoalW),
        ContactKind.Post, CollisionGroup.Goal, SOLID_FILTER,
      );
      addStatic(
        RAPIER.ColliderDesc.cylinder(GOAL_HEIGHT * 0.5, GOAL_POST_RADIUS)
          .setTranslation(lineX, GOAL_HEIGHT * 0.5, halfGoalW),
        ContactKind.Post, CollisionGroup.Goal, SOLID_FILTER,
      );
      // Crossbar: same cylinder laid along Z.
      addStatic(
        RAPIER.ColliderDesc.cylinder(halfGoalW, GOAL_POST_RADIUS)
          .setTranslation(lineX, GOAL_HEIGHT, 0)
          .setRotation(CROSSBAR_ROT),
        ContactKind.Crossbar, CollisionGroup.Goal, SOLID_FILTER,
      );

      // Nets: back, two sides, roof. These are what a 30 m/s shot tunnels
      // through without sweepBall (§3.4).
      addStatic(
        RAPIER.ColliderDesc.cuboid(NET_HALF_THICKNESS, GOAL_HEIGHT * 0.5, halfGoalW)
          .setTranslation(backX, GOAL_HEIGHT * 0.5, 0),
        ContactKind.Net, CollisionGroup.GoalNet, SOLID_FILTER,
      );
      for (let z = 0; z < 2; z++) {
        addStatic(
          RAPIER.ColliderDesc.cuboid(GOAL_DEPTH * 0.5, GOAL_HEIGHT * 0.5, NET_HALF_THICKNESS)
            .setTranslation(lineX + sign * GOAL_DEPTH * 0.5, GOAL_HEIGHT * 0.5, z === 0 ? -halfGoalW : halfGoalW),
          ContactKind.Net, CollisionGroup.GoalNet, SOLID_FILTER,
        );
      }
      addStatic(
        RAPIER.ColliderDesc.cuboid(GOAL_DEPTH * 0.5, NET_HALF_THICKNESS, halfGoalW)
          .setTranslation(lineX + sign * GOAL_DEPTH * 0.5, GOAL_HEIGHT, 0),
        ContactKind.Net, CollisionGroup.GoalNet, SOLID_FILTER,
      );

      // Goal sensor. Inner face is pushed 2*BALL_RADIUS past the line so that
      // "ball first touches the sensor" ⟺ "the WHOLE ball crossed the WHOLE
      // line" (§5.2 / Law 10) rather than firing on the leading edge, 22cm early.
      //
      // NOTE: this volume is currently inert — see the comment in
      // onIntersectionPair. ContactKind has no Goal member, so there is nothing
      // to report it as. Goal detection is ballCrossedGoalLine() in physics.ts.
      const sensorInnerX = lineX + sign * 2 * BALL_RADIUS;
      const sensorHalfDepth = (GOAL_DEPTH - 2 * BALL_RADIUS) * 0.5;
      if (sensorHalfDepth > 0) {
        addStatic(
          RAPIER.ColliderDesc.cuboid(sensorHalfDepth, GOAL_HEIGHT * 0.5, halfGoalW)
            .setTranslation(sensorInnerX + sign * sensorHalfDepth, GOAL_HEIGHT * 0.5, 0)
            .setSensor(true),
          ContactKind.None, CollisionGroup.GoalSensor, BALL_ONLY_FILTER,
        );
      }
    }

    // ── Boundary sensors (§5.2) ───────────────────────────────────────────
    // Same 2*BALL_RADIUS trick: the inner face sits where the ball's trailing
    // edge clears the line, so an intersection agrees with ballFullyCrossedLine()
    // rather than firing when the ball merely touches the line. They run out to
    // the edge of the representable volume so nothing can sweep past them, and
    // up to QUANT_POS_MAX_Y because a ball can leave play in the air.
    const sensorHalfY = QUANT_POS_MAX_Y * 0.5;
    const fullHalfZ = (QUANT_POS_MAX_Z - QUANT_POS_MIN_Z) * 0.5;
    const fullCentreZ = (QUANT_POS_MAX_Z + QUANT_POS_MIN_Z) * 0.5;
    const fullHalfX = (QUANT_POS_MAX_X - QUANT_POS_MIN_X) * 0.5;
    const fullCentreX = (QUANT_POS_MAX_X + QUANT_POS_MIN_X) * 0.5;

    // Goal lines (±X).
    for (let s = 0; s < 2; s++) {
      const sign = s === 0 ? 1 : -1;
      const inner = bounds.goalLineX + 2 * BALL_RADIUS;
      const outer = sign > 0 ? QUANT_POS_MAX_X : -QUANT_POS_MIN_X;
      if (outer > inner) {
        const half = (outer - inner) * 0.5;
        addStatic(
          RAPIER.ColliderDesc.cuboid(half, sensorHalfY, fullHalfZ)
            .setTranslation(sign * (inner + half), sensorHalfY, fullCentreZ)
            .setSensor(true),
          ContactKind.Boundary, CollisionGroup.Boundary, BALL_ONLY_FILTER,
        );
      }
    }
    // Touchlines (±Z).
    for (let s = 0; s < 2; s++) {
      const sign = s === 0 ? 1 : -1;
      const inner = bounds.touchlineZ + 2 * BALL_RADIUS;
      const outer = sign > 0 ? QUANT_POS_MAX_Z : -QUANT_POS_MIN_Z;
      if (outer > inner) {
        const half = (outer - inner) * 0.5;
        addStatic(
          RAPIER.ColliderDesc.cuboid(fullHalfX, sensorHalfY, half)
            .setTranslation(fullCentreX, sensorHalfY, sign * (inner + half))
            .setSensor(true),
          ContactKind.Boundary, CollisionGroup.Boundary, BALL_ONLY_FILTER,
        );
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Contact collection
  // ---------------------------------------------------------------------------

  /** The collider whose manifold onManifold is currently reading. Set
   *  immediately before the synchronous contactPair() call. */
  let pendingInfo: ColliderInfo | null = null;

  /**
   * Read one ball manifold.
   *
   * `flipped` is the subtle part. Rapier stores each pair in its own canonical
   * order, and manifold.normal() always points from ITS collider1 to ITS
   * collider2. flipped tells us our query order (ball, other) is reversed
   * relative to that. So:
   *   flipped  → internal1 = other, internal2 = ball → normal already points
   *              other→ball, which is "away from the surface". Use as-is.
   *   !flipped → internal1 = ball → normal points ball→other. Negate.
   * Verified both ways against a ground plane; getting this backwards would
   * reflect every bounce INTO the surface.
   */
  const onManifold = (m: RAPIER.TempContactManifold, flipped: boolean): void => {
    const info = pendingInfo;
    const state = liveState;
    if (!info || !state) return;

    const n = m.numContacts();
    if (n === 0) return;

    // One BallContact per collider pair. A sphere yields a single point per
    // manifold in practice; take the deepest if Rapier ever reports more.
    let bestI = -1;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < n; i++) {
      const d = m.contactDist(i);
      if (d < bestDist) {
        bestDist = d;
        bestI = i;
      }
    }
    if (bestI < 0) return;

    const c = acquireContact();
    if (!c) return; // pool exhausted; drop deterministically

    c.kind = info.kind;
    c.playerId = info.playerId;
    // Rapier reports `dist` as a signed distance: negative = overlapping.
    // BallContact.depth is the opposite sign. Rapier only produces contacts
    // within its prediction distance (~2mm), so a slightly NEGATIVE depth means
    // "within the prediction band but not yet touching" — that band is what
    // keeps `grounded` from flickering on a resting ball.
    c.depth = -bestDist;

    const sign = flipped ? 1 : -1;
    if (readRawVec(m.raw.normal() as RawVecLike, scratchNormal)) {
      c.nx = scratchNormal.x * sign;
      c.ny = scratchNormal.y * sign;
      c.nz = scratchNormal.z * sign;
    } else {
      c.nx = 0; c.ny = 0; c.nz = 0;
    }

    // The ball-side witness point, in the ball's local frame. Whichever index
    // the ball occupies internally is the one flipped selects.
    const rawP = flipped
      ? (m.raw.contact_local_p2(bestI) as RawVecLike | undefined)
      : (m.raw.contact_local_p1(bestI) as RawVecLike | undefined);
    if (readRawVec(rawP, scratchLocal)) {
      rotateByQuat(scratchWorld, ballRot, scratchLocal);
      c.px = state.ball.px + scratchWorld.x;
      c.py = state.ball.py + scratchWorld.y;
      c.pz = state.ball.pz + scratchWorld.z;
    } else {
      c.px = state.ball.px; c.py = state.ball.py; c.pz = state.ball.pz;
    }
  };

  const onContactPair = (otherHandle: number): void => {
    const info = colliderIndex.get(otherHandle);
    if (!info) return;
    pendingInfo = info;
    world.narrowPhase.contactPair(ballHandle, otherHandle, onManifold);
    pendingInfo = null;
  };

  const onIntersectionPair = (otherHandle: number): void => {
    const info = colliderIndex.get(otherHandle);
    const state = liveState;
    if (!info || !state) return;

    // Only boundary sensors are reportable. The goal sensors built above are
    // deliberately skipped: ContactKind has no Goal member, and reporting them
    // as Boundary would tell the rules a goal was a ball-out. Goals are decided
    // analytically by ballCrossedGoalLine() (physics.ts), which needs no sensor.
    if (info.kind !== ContactKind.Boundary) return;

    const c = acquireContact();
    if (!c) return;
    c.kind = ContactKind.Boundary;
    c.playerId = -1;
    // A sensor intersection produces no manifold, so there is no normal, no
    // witness point and no depth. Zeroed rather than faked.
    c.nx = 0; c.ny = 0; c.nz = 0;
    c.depth = 0;
    c.px = state.ball.px;
    c.py = state.ball.py;
    c.pz = state.ball.pz;
  };

  // ---------------------------------------------------------------------------
  // PhysicsWorld implementation
  // ---------------------------------------------------------------------------

  function syncAndStep(state: WorldState): void {
    liveState = state;

    const b = state.ball;
    scratchPos.x = b.px; scratchPos.y = b.py; scratchPos.z = b.pz;
    ballBody.setTranslation(scratchPos, true);
    ballRot.x = b.qx; ballRot.y = b.qy; ballRot.z = b.qz; ballRot.w = b.qw;
    ballBody.setRotation(ballRot, true);
    // The three lines that keep Rapier from owning the ball. See the header.
    ballBody.setLinvel(ZERO_VEC, true);
    ballBody.setAngvel(ZERO_VEC, true);

    const p = state.players;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      const body = playerBodies[i]!;
      const active = p.active[i] === 1 && p.sentOff[i] === 0 ? 1 : 0;
      if (active !== playerEnabled[i]) {
        body.setEnabled(active === 1);
        playerEnabled[i] = active;
      }
      if (active === 0) continue;
      scratchPos.x = p.px[i]!;
      scratchPos.y = p.py[i]! + PLAYER_CENTRE_OFFSET;
      scratchPos.z = p.pz[i]!;
      // Teleport, not setNextKinematicTranslation: `next` would leave the body at
      // last tick's position while the narrow phase runs, putting every ball-vs-
      // player contact a tick (up to 50cm at shot speed) behind reality.
      body.setTranslation(scratchPos, true);
      // Yaw is deliberately not applied: a capsule is rotationally symmetric
      // about Y, so it would be pure cost for an identical collision result.
    }

    world.step();

    contactCount = 0;
    world.narrowPhase.contactPairsWith(ballHandle, onContactPair);
    world.narrowPhase.intersectionPairsWith(ballHandle, onIntersectionPair);
    commitContactView();
  }

  function getBallContacts(): ReadonlyArray<BallContact> {
    return contactView;
  }

  function clearQuery(out: PhysicsQueryResult): void {
    out.hit = false;
    out.playerId = -1;
    out.kind = ContactKind.None;
    out.toi = 1;
    out.nx = 0; out.ny = 0; out.nz = 0;
    out.px = 0; out.py = 0; out.pz = 0;
  }

  /**
   * `d` is this tick's displacement, so the cast runs with shapeVel = d and
   * maxToi = 1. out.toi is therefore the FRACTION of `d` travelled before impact
   * (0..1) — the ball centre at impact is `from + d*toi`. out.p* is the contact
   * point on the surface, out.n* the outward normal of the surface hit.
   */
  function sweepBall(
    fromX: number, fromY: number, fromZ: number,
    dx: number, dy: number, dz: number,
    out: PhysicsQueryResult,
    excludePlayerId = -1,
  ): void {
    clearQuery(out);
    if (dx === 0 && dy === 0 && dz === 0) return;

    scratchPos.x = fromX; scratchPos.y = fromY; scratchPos.z = fromZ;
    scratchVel.x = dx; scratchVel.y = dy; scratchVel.z = dz;

    // ── The phantom ground ricochet, fixed at the primitive ────────────────
    // A resting ball sweeps from FLUSH on the pitch slab (py = BALL_RADIUS,
    // bottom exactly on y=0). Rapier's shape-cast reports that initial contact
    // at toi=0 with a DEGENERATE position-dependent normal (EPA at zero
    // separation — measured ny≈0.05 pointing sideways), and because it is the
    // smallest-toi hit it MASKS any real obstacle later in the same cast.
    // resolveSurface reflecting off that garbage normal was the phantom
    // ricochet: a struck ground pass flipped (measured (32,2,1.6) →
    // (−14.8,4.7,−21.7)) or was driven under the pitch (minBallY≈−0.067).
    //
    // When the ball starts flush and is NOT genuinely dropping the slab cannot
    // be meaningfully hit, so it is excluded from the cast — a post, wall or
    // player in the path is then reported with its true toi and normal. A real
    // downward sweep keeps the slab: a dropping ball must still bounce.
    //
    // "Genuinely dropping" tolerates the RESTING DESCENT: a rolling grounded
    // ball re-accumulates one tick of gravity every tick (vy ≈ −g·dt → dy ≈
    // −g·dt² ≈ −0.0027 m), and with `dy >= 0` as the gate that micro-descent
    // let the slab back into the cast at toi≈0 — freezing the ball's whole
    // horizontal displacement for that tick. Measured: a rolling ball lost its
    // motion every ~3rd tick (0.14, 0.14, 0.03, ...), ~15% of every ground
    // pass silently eaten and misread as rolling resistance. (§3.4)
    // stopAtPenetration=false additionally lets a cast that starts brushing a
    // surface it is leaving (post, player) ignore that contact.
    const flushOnSlab = fromY <= BALL_RADIUS + 1e-3 && dy >= -RESTING_DROP_PER_TICK;
    // Exclude the possessing carrier's capsule (see sweepExclude above).
    const carrier =
      excludePlayerId >= 0 && excludePlayerId < MAX_PLAYERS
        ? playerColliders[excludePlayerId]
        : undefined;
    sweepExclude.slab = flushOnSlab;
    sweepExclude.playerHandle = carrier !== undefined ? carrier.handle : -2;

    const hit = world.castShape(
      scratchPos, IDENTITY_ROT, scratchVel, ballShape,
      0, 1, false,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      interactionGroups(CollisionGroup.Ball, BALL_SWEEP_FILTER),
      // Without this the sweep instantly hits the ball's own collider at toi 0.
      ballCollider,
      undefined,
      flushOnSlab || carrier !== undefined ? sweepBallPredicate : undefined,
    );
    if (!hit) return;

    const info = colliderIndex.get(hit.collider.handle);
    out.hit = true;
    out.toi = hit.time_of_impact;
    out.playerId = info ? info.playerId : -1;
    out.kind = info ? info.kind : ContactKind.Ground;
    out.nx = hit.normal1.x; out.ny = hit.normal1.y; out.nz = hit.normal1.z;
    out.px = hit.witness1.x; out.py = hit.witness1.y; out.pz = hit.witness1.z;
  }

  /**
   * `from` is the capsule CENTRE (the caller supplies Rapier-style radius/
   * halfHeight, so it is working in capsule space, not the feet space used by
   * WorldState). toi is the fraction of `d`, as in sweepBall.
   */
  function sweepCapsule(
    fromX: number, fromY: number, fromZ: number,
    dx: number, dy: number, dz: number,
    radius: number, halfHeight: number,
    excludePlayerId: number,
    out: PhysicsQueryResult,
  ): void {
    clearQuery(out);
    if (dx === 0 && dy === 0 && dz === 0) return;

    // Mutate the cached shape rather than constructing one per call.
    sweepCapsuleShape.radius = radius;
    sweepCapsuleShape.halfHeight = halfHeight;

    scratchPos.x = fromX; scratchPos.y = fromY; scratchPos.z = fromZ;
    scratchVel.x = dx; scratchVel.y = dy; scratchVel.z = dz;

    const exclude =
      excludePlayerId >= 0 && excludePlayerId < MAX_PLAYERS
        ? playerColliders[excludePlayerId]
        : undefined;

    const hit = world.castShape(
      scratchPos, IDENTITY_ROT, scratchVel, sweepCapsuleShape,
      0, 1, true,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      interactionGroups(CollisionGroup.Player, CAPSULE_SWEEP_FILTER),
      exclude,
    );
    if (!hit) return;

    const info = colliderIndex.get(hit.collider.handle);
    out.hit = true;
    out.toi = hit.time_of_impact;
    out.playerId = info ? info.playerId : -1;
    out.kind = info ? info.kind : ContactKind.Ground;
    out.nx = hit.normal1.x; out.ny = hit.normal1.y; out.nz = hit.normal1.z;
    out.px = hit.witness1.x; out.py = hit.witness1.y; out.pz = hit.witness1.z;
  }

  /**
   * Collide-and-slide a player capsule.
   *
   * ⚠ CONTRACT READING — confirm this against player-controller.ts.
   * The interface names the input `dx,dy,dz` (a delta) and the output `x,y,z`
   * (not `dx,dy,dz`), so `out` is read here as the RESULTING WORLD POSITION in
   * WorldState convention (y = feet), not a corrected delta. Rapier's own
   * computedMovement() IS a delta; the conversion to a position happens here.
   * If the controller does `px[i] += out.x`, positions will double every tick —
   * loud, not silent, which is why this reading is safe to commit to.
   *
   * The base position comes from the live WorldState rather than from the
   * capsule, and that matters: stepPlayerCollisions() (step.ts stage 8) applies
   * the §3.3 separation impulse AFTER syncAndStep() has already written the
   * capsules, so a capsule-based base would silently discard the separation
   * every single tick and PLAYER_SEPARATION_IMPULSE would appear to do nothing.
   *
   * ⚠ Until the first syncAndStep(state) has run there is no WorldState here —
   * this method never receives one — and the capsules are still at the origin.
   * Prime with one syncAndStep(state) after spawning. See createPhysicsWorld.
   */
  function moveCharacter(
    playerId: number,
    dx: number, dy: number, dz: number,
    out: { x: number; y: number; z: number; grounded: boolean },
  ): void {
    const body = playerBodies[playerId];
    const collider = playerColliders[playerId];
    if (!body || !collider) {
      out.x = 0; out.y = 0; out.z = 0; out.grounded = false;
      return;
    }

    let baseX: number;
    let baseY: number;
    let baseZ: number;
    if (liveState) {
      baseX = liveState.players.px[playerId]!;
      baseY = liveState.players.py[playerId]!;
      baseZ = liveState.players.pz[playerId]!;
    } else {
      // Un-primed first tick only. Allocates, but runs at most once per player.
      const t = body.translation();
      baseX = t.x;
      baseY = t.y - PLAYER_CENTRE_OFFSET;
      baseZ = t.z;
    }

    scratchPos.x = baseX;
    scratchPos.y = baseY + PLAYER_CENTRE_OFFSET;
    scratchPos.z = baseZ;
    body.setTranslation(scratchPos, true);
    // REQUIRED: computeColliderMovement reads the COLLIDER's transform, which a
    // setTranslation on the body does not touch until something propagates it.
    // Without this the controller silently starts the sweep from the capsule's
    // previous position — measured walking a capsule straight through a wall.
    world.propagateModifiedBodyPositionsToColliders();

    scratchVel.x = dx; scratchVel.y = dy; scratchVel.z = dz;
    controller.computeColliderMovement(
      collider,
      scratchVel,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      interactionGroups(CollisionGroup.Player, CHARACTER_FILTER),
    );

    const mv = controller.computedMovement();
    out.x = baseX + mv.x;
    out.y = baseY + mv.y;
    out.z = baseZ + mv.z;
    out.grounded = controller.computedGrounded();
  }

  function dispose(): void {
    // Fly Machines are per-match (§12.2), but a server process may host several
    // matches in sequence and the WASM heap is not the JS heap — it will not be
    // collected for us.
    if (disposed) return;
    disposed = true;
    world.removeCharacterController(controller);
    world.free();
    staticBodies.length = 0;
    playerBodies.length = 0;
    playerColliders.length = 0;
    colliderIndex.clear();
    contactView.length = 0;
    contactPool.length = 0;
    liveState = null;
  }

  configurePitch(config);

  return {
    configurePitch,
    syncAndStep,
    getBallContacts,
    sweepBall,
    moveCharacter,
    sweepCapsule,
    dispose,
  };
}
